import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useWide } from "../hooks/useWide";
import { AnimatePresence, motion } from "motion/react";
import {
  AlertTriangle, Camera, History, Layers, LayoutDashboard, Loader2, MessagesSquare, MoreHorizontal, PanelLeftClose, ScanBarcode,
  SquarePen, Trash2, X,
} from "lucide-react";
import { SERVICES } from "../data/services";
import { deleteChat, hiddenVisuals, hideVisual, listChats, listVisuals, sized } from "../api/client";

const ICONS = { layers: Layers, messages: MessagesSquare };

/**
 * Chap panel: kompyuterda doimiy (yig'ish mumkin), telefonda chapdan
 * chiqadigan oyna. Tasvirlar ro'yxati serverdan — barcha qurilmalarniki.
 */
export function Sidebar({ drawer, onCloseDrawer, collapsed, onCollapse, ...props }) {
  const wide = useWide();

  if (wide) {
    return collapsed ? null : (
      <aside className="relative z-30 flex w-[272px] shrink-0 flex-col border-r border-sky-900/40 bg-[#04142b]/95">
        <Body {...props} onClose={onCollapse} closeIcon={PanelLeftClose} closeTitle="Panelni yig'ish" />
      </aside>
    );
  }

  return (
    <AnimatePresence>
      {drawer && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCloseDrawer}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed inset-y-0 left-0 z-50 flex w-[86%] max-w-[300px] flex-col border-r border-sky-900/40 bg-[#04142b] shadow-2xl"
          >
            <Body {...props} onClose={onCloseDrawer} closeIcon={X} closeTitle="Yopish" />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

const ITEM =
  "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition-colors";

function Body({
  onClose, closeIcon: CloseIcon, closeTitle,
  mode, onSelectMode, onNewChat, onPhoto,
  activeChat, onOpenChat, onDeletedChat, chatKey, onOpenVisual, refreshKey,
}) {
  return (
    <>
      <div className="flex shrink-0 items-center justify-between px-3 pb-2 pt-3">
        <button
          onClick={onNewChat}
          title="Yangi suhbat"
          className="flex cursor-pointer items-center gap-2.5 rounded-lg px-1 py-0.5 transition-opacity hover:opacity-80"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-sky-500/40 bg-sky-950/70 p-1">
            <img src="/logo.png" alt="" className="h-full w-full object-contain" />
          </div>
          <span className="text-sm font-bold tracking-wide text-white">AIExpert</span>
        </button>
        <button
          onClick={onClose}
          title={closeTitle}
          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>

      <nav className="shrink-0 space-y-0.5 px-2 pb-2">
        <button onClick={onNewChat} className={`${ITEM} text-slate-100 hover:bg-white/5`}>
          <SquarePen className="h-4 w-4 shrink-0 text-sky-300" /> Yangi suhbat
        </button>
        {SERVICES.map((s) => {
          const Icon = ICONS[s.icon] ?? Layers;
          const on = mode === s.id;
          return (
            <button
              key={s.id}
              onClick={() => onSelectMode(s.id)}
              className={`${ITEM} ${on ? "bg-sky-900/45 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}
            >
              <Icon className={`h-4 w-4 shrink-0 ${on ? "text-sky-300" : "text-slate-400"}`} />
              <span className="min-w-0 truncate">{s.title}</span>
            </button>
          );
        })}
        <button onClick={onPhoto} className={`${ITEM} text-slate-300 hover:bg-white/5 hover:text-white`}>
          <Camera className="h-4 w-4 shrink-0 text-slate-400" /> Tovarni foto orqali tahlil qilish
        </button>
        <a href="/dashboard" className={`${ITEM} text-slate-300 hover:bg-white/5 hover:text-white`}>
          <LayoutDashboard className="h-4 w-4 shrink-0 text-slate-400" /> Natijalar
        </a>
      </nav>

      <div className="feed-scroll min-h-0 flex-1 space-y-5 overflow-y-auto border-t border-sky-900/40 px-2 py-3">
        <Chats onOpen={onOpenChat} onDeleted={onDeletedChat} activeId={activeChat} refreshKey={chatKey} />
        <Visuals onOpen={onOpenVisual} refreshKey={refreshKey} />
      </div>
    </>
  );
}

// `uz-UZ` oy nomi ko'p brauzerda yo'q ("M09 30") — sana raqam bilan
const when = (iso) => {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  const hm = `${p(d.getHours())}:${p(d.getMinutes())}`;
  return d.toDateString() === new Date().toDateString() ? hm : `${p(d.getDate())}.${p(d.getMonth() + 1)} ${hm}`;
};

/** Suhbatlar tarixi — serverdan, barcha qurilmalarniki; bosilsa suhbat qayta ochiladi, "…" — amallar. */
function Chats({ onOpen, onDeleted, activeId, refreshKey }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [menu, setMenu] = useState(null); // {id, anchor}
  const [ask, setAsk] = useState(null); // o'chirishni tasdiqlash: {id, title}

  useEffect(() => {
    const load = () => listChats(50).then(setItems).catch((e) => setError(e.message || String(e)));
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [refreshKey]);

  const remove = async (id) => {
    await deleteChat(id);
    setItems((xs) => xs.filter((c) => c.id !== id));
    setAsk(null);
    onDeleted?.(id);
  };

  return (
    <section>
      <h3 className="mb-1 flex items-center gap-1.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <History className="h-3 w-3" /> Suhbatlar
      </h3>
      {error && <p className="px-3 text-xs text-rose-300">{error}</p>}
      {!items && !error && <Loader2 className="mx-3 h-4 w-4 animate-spin text-slate-500" />}
      {items?.length === 0 && <p className="px-3 text-xs text-slate-600">Hozircha bo'sh</p>}
      {items?.map((c) => {
        const on = c.id === activeId;
        const open = menu?.id === c.id;
        return (
          <div
            key={c.id}
            className={`group flex items-center rounded-lg transition-colors ${
              on ? "bg-sky-900/45 text-white" : open ? "bg-white/5 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
            }`}
          >
            <button onClick={() => onOpen(c.id)} className="min-w-0 flex-1 truncate py-1.5 pl-3 text-left text-[13px]">
              {c.title || "Suhbat"}
            </button>
            {/* Sichqonchada — ustiga kelganda; telefonda (hover yo'q) doim ko'rinadi */}
            <button
              onClick={(e) => setMenu(open ? null : { id: c.id, anchor: e.currentTarget })}
              aria-label="Suhbat amallari"
              title="Amallar"
              className={`mr-1 shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-white/10 hover:text-white focus-visible:opacity-100 ${
                on || open ? "" : "pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100"
              }`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </div>
        );
      })}
      {menu && (
        <RowMenu
          anchor={menu.anchor}
          onDelete={() => {
            setAsk({ id: menu.id, title: items.find((c) => c.id === menu.id)?.title || "Suhbat" });
            setMenu(null);
          }}
          onClose={() => setMenu(null)}
        />
      )}
      <AnimatePresence>
        {ask && (
          <ConfirmDelete
            heading="Suhbat o'chirilsinmi?"
            title={ask.title}
            onConfirm={() => remove(ask.id)}
            onClose={() => setAsk(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

/** "…" menyusi: panel ichidagi aylantirish qirqmasin — `body` ga, tugma yoniga chiziladi. */
function RowMenu({ anchor, onDelete, onClose }) {
  const ref = useRef(null);
  const r = anchor.getBoundingClientRect();
  const below = r.bottom + 80 < window.innerHeight;
  const pos = { right: Math.max(8, window.innerWidth - r.right), ...(below ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }) };

  useEffect(() => {
    const out = (e) => !ref.current?.contains(e.target) && !anchor.contains(e.target) && onClose();
    const key = (e) => e.key === "Escape" && onClose();
    document.addEventListener("pointerdown", out);
    window.addEventListener("keydown", key);
    window.addEventListener("resize", onClose);
    window.addEventListener("scroll", onClose, true); // panel suriladi — menyu tugmadan ajralib qolmasin
    return () => {
      document.removeEventListener("pointerdown", out);
      window.removeEventListener("keydown", key);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: "fixed", ...pos }}
      className="z-[60] w-44 rounded-xl border border-sky-800/60 bg-[#061a36] p-1 font-sans text-sm shadow-2xl shadow-black/50"
    >
      <button
        role="menuitem"
        onClick={onDelete}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-rose-300 hover:bg-rose-950/40"
      >
        <Trash2 className="h-4 w-4" /> O'chirish
      </button>
    </div>,
    document.body,
  );
}

/** O'chirishni tasdiqlash — ekran o'rtasida; Esc yoki fon bosilsa bekor bo'ladi. */
function ConfirmDelete({ heading, title, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const yes = useRef(null);

  useEffect(() => {
    yes.current?.focus();
    const key = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [busy, onClose]);

  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      await onConfirm();
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      onPointerDown={(e) => e.target === e.currentTarget && !busy && onClose()}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 font-sans backdrop-blur-sm"
    >
      <motion.div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="del-title"
        initial={{ opacity: 0, scale: 0.94, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 4 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm rounded-2xl border border-sky-800/60 bg-[#061a36] p-5 text-slate-100 shadow-2xl shadow-black/60"
      >
        <h2 id="del-title" className="text-base font-semibold text-white">{heading}</h2>
        <p className="mt-2 line-clamp-2 text-sm text-slate-300">{title}</p>
        {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-xl px-4 py-2 text-sm text-slate-300 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-50"
          >
            Bekor qilish
          </button>
          <button
            ref={yes}
            onClick={confirm}
            disabled={busy}
            className="flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-500 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} O'chirish
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

/** Barcha qurilmalardan yuborilgan tasvirlar — serverdan; "…" bilan ro'yxatdan o'chiriladi (yumshoq). */
function Visuals({ onOpen, refreshKey }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [menu, setMenu] = useState(null); // {id, anchor}
  const [ask, setAsk] = useState(null); // {id, title}

  // Yangi tasvir shu qurilmada tugaganda (refreshKey) va oynaga qaytilganda
  // (boshqa qurilmadan yuborilganlari) yangilanadi. Yashirilganlar o'rnini
  // to'ldirish uchun ko'proq olinadi
  useEffect(() => {
    const load = () =>
      Promise.all([listVisuals(100), hiddenVisuals().catch(() => [])])
        .then(([vs, hidden]) => setItems(vs.filter((v) => !hidden.includes(v.id)).slice(0, 50)))
        .catch((e) => setError(e.message || String(e)));
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [refreshKey]);

  const name = (v) => v.product?.name || v.product_hint || "Foto tahlili";

  const remove = async (id) => {
    await hideVisual(id);
    setItems((xs) => xs.filter((v) => v.id !== id));
    setAsk(null);
  };

  return (
    <section>
      <h3 className="mb-1 flex items-center gap-1.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <Camera className="h-3 w-3" /> Foto tahlillari
      </h3>
      {error && <p className="px-3 text-xs text-rose-300">{error}</p>}
      {!items && !error && <Loader2 className="mx-3 h-4 w-4 animate-spin text-slate-500" />}
      {items?.length === 0 && <p className="px-3 text-xs text-slate-600">Hozircha bo'sh</p>}
      {items?.map((v) => {
        const open = menu?.id === v.id;
        return (
          <div
            key={v.id}
            className={`group flex items-center rounded-xl transition-colors ${open ? "bg-white/5" : "hover:bg-white/5"}`}
          >
            <button onClick={() => onOpen(v.id)} className="flex min-w-0 flex-1 items-center gap-2.5 py-1.5 pl-2 text-left">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/5">
                {v.status === "done" && v.files ? (
                  <img src={sized(v.files.image, 160)} alt="" loading="lazy" className="h-full w-full bg-white object-contain" />
                ) : v.status === "done" ? (
                  <ScanBarcode className="h-4 w-4 text-sky-300/80" /> // faqat tahlil va kodlar, rasm chizilmagan
                ) : v.status === "failed" ? (
                  <AlertTriangle className="h-4 w-4 text-rose-300/80" />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-sky-300/70" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-slate-200">{name(v)}</span>
                <span className={`block text-[11px] ${v.status === "failed" ? "text-rose-300/70" : "text-slate-500"}`}>
                  {when(v.created_at)}
                  {v.status === "failed" ? " · yiqildi" : v.status === "done" ? "" : " · jarayonda"}
                </span>
              </span>
            </button>
            <button
              onClick={(e) => setMenu(open ? null : { id: v.id, anchor: e.currentTarget })}
              aria-label="Foto tahlili amallari"
              title="Amallar"
              className={`mr-1 shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-white/10 hover:text-white focus-visible:opacity-100 ${
                open ? "" : "pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100"
              }`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </div>
        );
      })}
      {menu && (
        <RowMenu
          anchor={menu.anchor}
          onDelete={() => {
            const v = items.find((x) => x.id === menu.id);
            setAsk({ id: menu.id, title: v ? name(v) : "Foto tahlili" });
            setMenu(null);
          }}
          onClose={() => setMenu(null)}
        />
      )}
      <AnimatePresence>
        {ask && (
          <ConfirmDelete
            heading="Foto tahlili o'chirilsinmi?"
            title={ask.title}
            onConfirm={() => remove(ask.id)}
            onClose={() => setAsk(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
